import { InfoIcon, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@sandtech-hub-monorepo/ui-kit';

import { SubscriptionState } from '../../../data/useSubscriptionState';
import { AppRoutesPaths, subscribePath } from '../../../routes/types';
import { plural } from '../../../utils/plural';

interface Props {
  state: SubscriptionState;
  /** Whole days left on a cancelled subscription's paid period. */
  daysRemaining: number | null;
  /**
   * The program to buy plans for, when the surface has one. The dashboard
   * passes the learner's enrolled program so the plans page is scoped to it;
   * the path chooser has no enrollment yet and passes nothing, which lands on
   * the unscoped plans page — the entry the plans page is built to handle.
   */
  programId: string | undefined;
  /**
   * The subscription read is still in flight. `state` defaults to `'none'`
   * until it resolves, so rendering on that default flashes the upsell copy
   * at a learner who turns out to be subscribed the moment data lands.
   */
  loading?: boolean;
}

interface BannerCopy {
  title: (daysRemaining: number | null) => string;
  body: string;
  cta: string;
  locked: boolean;
  /**
   * Settings for anything that still holds the one-per-plan slot: the server
   * refuses to sell a second subscription over it — "There is already a
   * subscription for this plan on your account" — so the plans page would be a
   * dead end.
   */
  toSettings: boolean;
}

const BANNER_COPY: Record<Exclude<SubscriptionState, 'active'>, BannerCopy> = {
  none: {
    title: () => 'Unlock All-Access',
    body: 'Upgrade to access all programs.',
    cta: 'Subscribe to All Access',
    locked: false,
    toSettings: false,
  },
  expiring: {
    title: (daysRemaining) =>
      daysRemaining == null
        ? 'Your subscription is ending'
        : `Your subscription ends in ${daysRemaining} ${plural(
            daysRemaining,
            'day',
            'days'
          )}`,
    body: 'You have cancelled it. Paid modules stay open until then.',
    cta: 'Manage subscription',
    locked: false,
    toSettings: true,
  },
  'past-due': {
    title: () => 'Your last payment failed',
    body: 'Stripe is retrying and your access continues meanwhile. Update your card to keep it.',
    cta: 'Update payment details',
    locked: false,
    toSettings: true,
  },
  'payment-failed': {
    title: () => 'Your payment didn’t go through',
    body: 'Your subscription has not started and nothing has been charged. Finish the payment in your billing settings to open the paid modules.',
    cta: 'Finish your payment',
    locked: true,
    toSettings: true,
  },
  blocked: {
    title: () => 'Your subscription needs attention',
    body: 'It is not active, and a new one cannot be started while it exists. Sort it out in your billing settings.',
    cta: 'Manage subscription',
    locked: true,
    toSettings: true,
  },
  ended: {
    title: () => 'Your All-Access subscription has ended',
    body: 'Paid modules are locked again. Your progress is kept.',
    cta: 'Subscribe to All Access',
    locked: true,
    toSettings: false,
  },
};

/**
 * Subscription-lifecycle banner above the dashboard.
 *
 * The destination is the part that matters, and it is decided by whether a
 * subscription still holds the plan slot — see BANNER_COPY above. Renders
 * nothing while a subscription is simply working: a healthy one renews in
 * silence and there is nothing to tell anyone.
 */
export const SubscriptionBanner = ({
  state,
  daysRemaining,
  programId,
  loading = false,
}: Props) => {
  if (loading || state === 'active') return null;

  const { title, body, cta, locked, toSettings } = BANNER_COPY[state];

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-saffron/40 bg-saffron/10 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {locked ? (
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
        ) : (
          <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
        )}
        <div>
          <p className="font-semibold text-heading">{title(daysRemaining)}</p>
          <p className="text-sm text-muted-foreground">{body}</p>
        </div>
      </div>
      <Button
        asChild
        className="shrink-0 rounded-full bg-saffron px-6 text-foreground hover:bg-saffron-active"
      >
        <Link
          to={toSettings ? AppRoutesPaths.Settings : subscribePath(programId)}
        >
          {cta}
        </Link>
      </Button>
    </div>
  );
};
